import React, { useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import Button from 'shared/components/UC/Button';
import Toast from 'shared/components/Toast';
import evaluatorService from '../services/evaluatorService';
import authService from '../services/authService';
import { useAuth } from '../contexts/AuthContext';
import styles from './PasswordSetup.module.css';

const PasswordSetup: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { setAuthData } = useAuth();

  const token = searchParams.get('token');
  const email = searchParams.get('email');

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [requestingNewToken, setRequestingNewToken] = useState(false);
  const [toast, setToast] = useState<{
    message: string;
    type: 'error' | 'success' | 'warning' | 'info';
  } | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!token || !email) {
      setToast({
        message: 'Código o email faltante. Verifica el enlace.',
        type: 'error',
      });
      return;
    }

    if (password.length < 6) {
      setToast({
        message: 'La contraseña debe tener al menos 6 caracteres',
        type: 'warning',
      });
      return;
    }

    if (password !== confirmPassword) {
      setToast({
        message: 'Las contraseñas no coinciden',
        type: 'warning',
      });
      return;
    }

    try {
      setLoading(true);
      await evaluatorService.setupPassword({
        email,
        token,
        password,
      });

      // Log the user in with the credentials they just created — no
      // detour through the login page.
      const session = await authService.sessionFromSetup(email);
      localStorage.setItem('auth_token', session.token);
      localStorage.setItem('auth_user', JSON.stringify(session.user));
      setAuthData(session.user, session.token);

      setToast({
        message: 'Contraseña configurada exitosamente. Entrando a tu panel...',
        type: 'success',
      });

      setTimeout(() => {
        navigate('/dashboard', { replace: true });
      }, 1200);
    } catch (error) {
      setToast({
        message:
          error instanceof Error
            ? error.message
            : 'Error al configurar la contraseña',
        type: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleRequestNewToken = async () => {
    if (!email) {
      setToast({
        message: 'Email no disponible para solicitar nuevo token',
        type: 'error',
      });
      return;
    }

    try {
      setRequestingNewToken(true);
      await evaluatorService.requestPasswordSetup({ email });

      setToast({
        message:
          'Se ha enviado un nuevo email de configuración a tu dirección de correo.',
        type: 'success',
      });
    } catch (error) {
      setToast({
        message:
          error instanceof Error
            ? error.message
            : 'Error al solicitar nuevo token',
        type: 'error',
      });
    } finally {
      setRequestingNewToken(false);
    }
  };

  if (!token || !email) {
    return (
      <div className={styles.container}>
        <div className={styles.card}>
          <div className={styles.header}>
            <h1>Enlace Inválido</h1>
            <p>El enlace de configuración no es válido o ha expirado.</p>
          </div>
          <div className={styles.content}>
            <p className={styles.helpText}>
              Código o email faltante en la URL. Verifica el enlace recibido por
              email.
            </p>
            <div className={styles.actions}>
              <Button variant="secondary" onClick={() => navigate('/login')}>
                Ir al Login
              </Button>
            </div>
          </div>
        </div>

        {toast && (
          <Toast
            message={toast.message}
            type={toast.type}
            onClose={() => setToast(null)}
          />
        )}
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.card}>
        <div className={styles.header}>
          <h1>Configurar Contraseña</h1>
          <p>
            Configura tu contraseña para acceder a la plataforma de evaluación
          </p>
          <div className={styles.userInfo}>
            <strong>Email:</strong> {email}
          </div>
        </div>

        <form onSubmit={handleSubmit} className={styles.form}>
          <div className={styles.formGroup}>
            <label htmlFor="password" className={styles.label}>
              Nueva Contraseña *
            </label>
            <input
              type="password"
              id="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className={styles.input}
              placeholder="Mínimo 6 caracteres"
              required
              disabled={loading}
              minLength={6}
            />
          </div>

          <div className={styles.formGroup}>
            <label htmlFor="confirmPassword" className={styles.label}>
              Confirmar Contraseña *
            </label>
            <input
              type="password"
              id="confirmPassword"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              className={styles.input}
              placeholder="Repite la contraseña"
              required
              disabled={loading}
              minLength={6}
            />
          </div>

          <div className={styles.requirements}>
            <h4>Requisitos de la contraseña:</h4>
            <ul>
              <li className={password.length >= 6 ? styles.valid : ''}>
                Al menos 6 caracteres
              </li>
              <li
                className={
                  password === confirmPassword && password ? styles.valid : ''
                }
              >
                Las contraseñas deben coincidir
              </li>
            </ul>
          </div>

          <div className={styles.actions}>
            <Button
              type="submit"
              variant="primary"
              disabled={
                loading || password.length < 6 || password !== confirmPassword
              }
              className={styles.submitButton}
            >
              {loading ? 'Configurando...' : 'Configurar Contraseña'}
            </Button>
          </div>
        </form>

        <div className={styles.helpSection}>
          <p className={styles.helpText}>
            ¿Problemas con el enlace?{' '}
            <button
              type="button"
              onClick={handleRequestNewToken}
              disabled={requestingNewToken}
              className={styles.linkButton}
            >
              Solicitar nuevo enlace
            </button>
          </p>
        </div>
      </div>

      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}
    </div>
  );
};

export default PasswordSetup;
